import { useState, useEffect } from 'react';

interface UseCameraCleanupOptions {
  /**
   * Time threshold in milliseconds to determine if camera cleanup is needed
   * @default 30000 (30 seconds)
   */
  cleanupThreshold?: number;
  /**
   * Delay before performing the page refresh in milliseconds
   * @default 500
   */
  refreshDelay?: number;
  /**
   * Whether to automatically perform cleanup on hook initialization
   * @default true
   */
  autoCleanup?: boolean;
}

interface UseCameraCleanupReturn {
  /**
   * Whether the camera is currently being force-stopped
   */
  isForceStoppingCamera: boolean;
  /**
   * Manually trigger camera cleanup
   */
  performCameraCleanup: () => Promise<void>;
  /**
   * Update the last camera access timestamp
   */
  updateLastCameraAccess: () => void;
}

/**
 * Custom hook for managing camera cleanup to prevent camera resource leaks
 * when navigating between pages that use camera functionality.
 */
export const useCameraCleanup = (options: UseCameraCleanupOptions = {}): UseCameraCleanupReturn => {
  const {
    cleanupThreshold = 30000, // 30 seconds
    refreshDelay = 500,
    autoCleanup = true
  } = options;

  const [isForceStoppingCamera, setIsForceStoppingCamera] = useState(false);

  const performCameraCleanup = async (): Promise<void> => {
    // Check if we've already attempted a refresh for camera cleanup
    const hasRefreshedForCamera = sessionStorage.getItem('cameraRefreshAttempted');
    
    // Only attempt refresh once per session to prevent infinite loops
    if (hasRefreshedForCamera) {
      return;
    }

    try {
      // Check if any media devices are currently active
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      // Check if there are any active media streams
      // This is a heuristic check since we can't directly query active streams
      if (videoDevices.length > 0) {
        // Try to detect if camera might still be active by checking for recent camera access
        // We'll use a time-based approach combined with localStorage check
        const lastCameraAccess = localStorage.getItem('lastCameraAccess');
        const currentTime = Date.now();
        
        if (lastCameraAccess) {
          const timeSinceAccess = currentTime - parseInt(lastCameraAccess);
          
          // If camera was accessed within the threshold time, perform refresh
          if (timeSinceAccess < cleanupThreshold) {
            console.log('Performing camera cleanup refresh...');
            
            // Mark that we've attempted a refresh
            sessionStorage.setItem('cameraRefreshAttempted', 'true');

            // Set flag to indicate we're force-stopping camera
            setIsForceStoppingCamera(true);
            
            // Small delay before refresh to allow component to mount properly
            setTimeout(() => {
              window.location.reload();
            }, refreshDelay);
            
            return;
          }
        }
      }
    } catch (error) {
      // If we can't check media devices, skip the refresh mechanism
      console.log('Media devices check failed, skipping camera refresh');
    }
  };

  const updateLastCameraAccess = (): void => {
    localStorage.setItem('lastCameraAccess', Date.now().toString());
  };

  useEffect(() => {
    if (autoCleanup) {
      // Set timestamp for when we access this page (coming from camera page)
      updateLastCameraAccess();
      
      // Attempt camera cleanup
      performCameraCleanup();
    }
  }, [autoCleanup, cleanupThreshold, refreshDelay]);

  return {
    isForceStoppingCamera,
    performCameraCleanup,
    updateLastCameraAccess
  };
}; 